import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { logger } from '../../utils/logger';
import PageHeader from '../common/PageHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { USERNAME_MIN_LENGTH, USERNAME_MAX_LENGTH } from '../../types/user';
import LoadingScreen from '../common/LoadingScreen';
import '../../styles/Auth.css';

/**
 * Username selection component shown after sign in if user doesn't have a username
 */
const ChooseUsername: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { 
    currentUser, 
    hasUsername, 
    checkUsernameExists, 
    setUserUsername,
    loading,
    profileLoading 
  } = useAuth();
  const navigate = useNavigate();
    // Check if user is logged in and already has a username
  useEffect(() => {
    if (!currentUser) {
      navigate('/sign-in');
      return;
    }
    
    // Don't show the component at all if we're still loading data
    if (loading || profileLoading) {
      // Still loading, don't make any decisions yet
      return;
    }
    
    // Now that we're not loading, if the user has a username, redirect
    if (hasUsername) {
      logger.info('👤 User already has a username, redirecting to profile', { hasUsername });
      navigate('/my-profile');
    }
  }, [currentUser, hasUsername, navigate, loading, profileLoading]);

  // Debounced username validation
  useEffect(() => {
    // Reset validation states when username changes
    setIsValid(null);
    setError('');
    
    // Don't check empty usernames or too short usernames
    if (!username || username.length < USERNAME_MIN_LENGTH) {
      return;
    }
    
    // Create a timer for debounce
    const timer = setTimeout(async () => {
      try {
        setIsChecking(true);
        logger.info('🔍 Checking username availability', { username });
        
        const exists = await checkUsernameExists(username);
        setIsValid(!exists);
        
        if (exists) {
          setError('Username is already taken');
        }
      } catch (err: any) {
        logger.error('❌ Error checking username', err);
        setError('Error checking username availability');
        setIsValid(false);
      } finally {
        setIsChecking(false);
      }
    }, 500); // 500ms debounce
    
    // Cleanup timer on component unmount or when username changes
    return () => clearTimeout(timer);
  }, [username, checkUsernameExists]);

  // Handle local username validation
  const validateUsername = (): boolean => {
    // Check length
    if (username.length < USERNAME_MIN_LENGTH) {
      setError(`Username must be at least ${USERNAME_MIN_LENGTH} characters`);
      return false;
    }
    
    if (username.length > USERNAME_MAX_LENGTH) {
      setError(`Username must be at most ${USERNAME_MAX_LENGTH} characters`);
      return false;
    }
    
    // Check format (alphanumeric and underscores only)
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setError('Username can only contain letters, numbers, and underscores');
      return false;
    }
    
    // Check if username is valid (from server validation)
    if (isValid === false) {
      return false;
    }
    
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    
    // First do local validation
    if (!validateUsername()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError('');
      
      await setUserUsername(username);
      logger.success('✅ Username set successfully', { username });
      
      // Navigate to profile page after username is set
      navigate('/my-profile');
    } catch (err: any) {
      logger.error('❌ Error setting username', err);
      setError(err.message || 'Failed to set username. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If we're still loading auth or profile data, show loading screen
  if (loading || profileLoading) {
    return <LoadingScreen message="Checking profile data..." />;
  }

  return (
    <>      <PageHeader
        title="Choose Your Username"
        subtitle="This is how others will see you"
      />      <div className="auth-form-container">
        <h2 className="text-center">Choose Your Username</h2>
        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="username-form">          
          <div className="form-group username-input-group">
            <div className="username-input-container">
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Choose your public username"
                disabled={isSubmitting}
                aria-describedby="usernameHint"
                className={
                  isValid === true ? 'valid' : isValid === false ? 'invalid' : ''
                }
                autoComplete="off"
                required
              />
              <div className="username-status">
                {isChecking ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="checking-icon" />
                ) : isValid === true ? (
                  <FontAwesomeIcon icon={faCheck} className="valid-icon" />
                ) : isValid === false ? (
                  <FontAwesomeIcon icon={faTimes} className="invalid-icon" />
                ) : null}
              </div>
            </div>
            <small id="usernameHint" className="form-hint">
              This will be your public identity. Choose something you like - you can change it later in settings.
              Must be {USERNAME_MIN_LENGTH}-{USERNAME_MAX_LENGTH} characters, letters, numbers, and underscores only.
            </small>
          </div>

          <div className="auth-section-divider"></div>          <div className="username-info">
            <p><strong>Important:</strong> You can change your username anytime in your profile settings.</p>
            <ul>
              <li>Usernames are unique across the platform</li>
              <li>Only letters, numbers, and underscores are allowed</li>
              <li>This will be displayed in all public areas of the site</li>
            </ul>
          </div>          <button
            type="submit"
            className="primary-button"
            disabled={isSubmitting || isValid !== true}
          >
            {isSubmitting ? 'Setting Username...' : 'Set Username'}
          </button>
        </form>
      </div>
    </>
  );
};

export default ChooseUsername;
